////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Short (~50vh) free-floating page-title hero (1.0.3.md §4,       //
//           round-2 design tweaks 12/08/2026)                                //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Every non-Home public page's hero — the "shorter" variant of Home's hero
//  §4 calls for: a page title, word-by-word `maskRevealWord` (the same reveal
//  grammar Home's H1 uses), free-floating over the persistent, per-route
//  <BackgroundLayer /> (mounted once in PublicLayout, already resolving its
//  own `bgi-<page>` asset for every route this component is used on — this
//  component does not render an image of its own, deliberately: a second,
//  page-local background image would fight the one BackgroundLayer already
//  owns). Per §5's explicit exception, this is free-floating text — NEVER
//  wrapped in a <GlassTile>.
//
//  This is the page's single <h1> — callers must not render their own.
//
//  ROUND-2 REVERT (12/08/2026): the previous pass's `position: sticky` +
//  scroll-linked shrink experiment ("pinned titles") is rolled back per
//  Mike's own call — it left "a big semi-transparent deadspace above [the
//  title] which shrinks to nothing", worse than the plain free-floating
//  title it replaced. This file is back to a single static section; no
//  spacer, no sticky box, no scroll-linked transform. `~50vh` (down from
//  ~60vh) is this pass's own retune, not a leftover of the pinning work.
//
//  ROUND-2 VEIL MOVE (12/08/2026): this component used to carry its own
//  dark, always-black scrim (`bg-gradient-to-t from-black/55 ...`) sized to
//  its own ~60vh section. That scrim is GONE — every non-Home public page
//  now gets a theme-aware, UNIFORM, FULL-PAGE veil from <BackgroundLayer />
//  itself (see that file's own MARK), rather than a per-hero band that used
//  to visibly end at the hero's own bottom edge. Title text follows suit:
//  `text-ink` (theme-flipping token), not a forced `text-white` — ink is
//  legible over BackgroundLayer's veil in BOTH themes, verified numerically
//  in test/contrast.hero-veil.test.ts across the full imagery grey range,
//  for both `treated` (CSS-monochrome placeholder) and untreated (Mike's
//  real `bgi-about-*.webp` pair) backgrounds.
//
////////////////////////////////////////////////////////////////////////////////

import { Fragment, type ReactNode } from 'react';
import { motion } from 'framer-motion';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { fadeRise, maskRevealWord, reduceVariants, staggerGroup } from '@/lib/motion';

export interface PageHeroProps {
  /** Rendered as the page's <h1>, split on whitespace and revealed one word at
   * a time. Kept as a single string (not markup) — every word gets its own
   * `overflow-hidden` clip wrapper, which markup spanning word boundaries would
   * break. */
  title: string;
  /** Optional supporting line — `text-body-tertiary`, fades in once the title
   * has finished (same stagger group, next in sequence). */
  subtitle?: ReactNode;
  id?: string;
}

export function PageHero ({ title, subtitle, id }: PageHeroProps) {
  const reducedMotion = useReducedMotion();
  const groupVariants = reduceVariants(staggerGroup, reducedMotion);
  const wordVariants = reduceVariants(maskRevealWord, reducedMotion);
  const subtitleVariants = reduceVariants(fadeRise, reducedMotion);
  const words = title.split(' ').filter(Boolean);

  return (
    <section aria-label="Page title" className="relative flex min-h-[50vh] items-center overflow-hidden px-6 py-16">
      <motion.div
        className="relative z-10 mx-auto w-full max-w-6xl"
        initial="hidden"
        animate="visible"
        variants={groupVariants}
      >
        <h1 id={id} className="h1-section flex flex-wrap text-ink">
          {words.map((word, index) => (
            // Each word gets its own clip mask — `maskRevealWord`'s
            // `clipPath: inset(0% 0% 100% 0%)` -> `inset(0% 0% 0% 0%)` baseline
            // reveal needs a per-word `overflow-hidden` boundary, or the whole
            // line would clip as one block instead of rising word by word. The
            // literal `' '` text node AFTER (not inside) that clip boundary is
            // deliberate — without a real whitespace text node between words,
            // the accessible name computed from this heading's textContent
            // reads as one run-together word ("WelcomeToBreeseStreet"), since
            // adjacent JSX elements render with no whitespace of their own; and
            // a space placed INSIDE the clipped span would itself be subject to
            // the same clip-path transform as the word.
            <Fragment key={`${word}-${index}`}>
              <span className="mr-[0.1em] overflow-hidden pb-[0.08em]">
                <motion.span className="inline-block" variants={wordVariants}>
                  {word}
                </motion.span>
              </span>
              {index < words.length - 1 ? ' ' : ''}
            </Fragment>
          ))}
        </h1>

        {/* No `!text-*` override needed here (round-2, unlike the 1.0.3 forced-
            white era): `.text-body-tertiary`'s `--color-muted` clears WCAG AA
            over BackgroundLayer's uniform veil in both themes at the veil's
            tuned opacity — see test/contrast.hero-veil.test.ts. */}
        {/* div, not p — subtitle accepts ReactNode (career facts render a
            <dl> here), and block content inside <p> is invalid HTML. */}
        {subtitle && (
          <motion.div className="hero-subtitle mt-6 max-w-2xl" variants={subtitleVariants}>
            {subtitle}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
